import { createStore, get, set, del, entries } from 'idb-keyval';
import { api, AuthExpiredError, type Measurement, type Settings } from './api';
import type { Site } from './cluster';

const queue = createStore('antenne-outbox', 'measurements');
const cache = createStore('antenne-cache', 'kv');

export const outbox = {
  /** Save locally first; same id replaces the queued copy. */
  async enqueue(m: Measurement): Promise<void> {
    await set(m.id, m, queue);
  },

  async pending(): Promise<Measurement[]> {
    const all = (await entries<string, Measurement>(queue)).map(([, v]) => v);
    return all.sort((a, b) => a.takenAt.localeCompare(b.takenAt) || a.id.localeCompare(b.id));
  },

  async remove(id: string): Promise<void> {
    await del(id, queue);
  },

  /** Send oldest first; drop an item only after the server accepted it; stop at the first error. */
  async flush(put: (m: Measurement) => Promise<void> = api.putMeasurement): Promise<{ sent: number; left: number; authExpired: boolean }> {
    const items = await this.pending();
    let sent = 0;
    let authExpired = false;
    for (const m of items) {
      try {
        await put(m);
      } catch (e) {
        authExpired = e instanceof AuthExpiredError;
        break;
      }
      await del(m.id, queue);
      sent++;
    }
    return { sent, left: items.length - sent, authExpired };
  },
};

/** Last data seen from the server, so the app opens on the roof without network. */
export const offlineCache = {
  getSites: () => get<Site[]>('sites', cache),
  setSites: (s: Site[]) => set('sites', s, cache),
  getSettings: () => get<Settings>('settings', cache),
  setSettings: (s: Settings) => set('settings', s, cache),
  getMeasurements: () => get<Measurement[]>('measurements', cache),
  setMeasurements: (m: Measurement[]) => set('measurements', m, cache),
};
